// Constants
import { ELIGIBILITY_TYPE, HOLDING_PERIOD, RULES_LOGIC } from "~constants/perks"

// Utils
import { createDropdownValue, SelectOption } from "~utils/dropdown"
import { ROLES } from "~rbac/constants/rbac-roles"
import { Security } from "~shapes/portfolio"

const booleanSymbolsRegexp = /(>=|<=|==|!=)/g
const logicSymbolsRegexp = /(&&|AND|OR)/g

export interface EligibilityRuleParsed {
  period?: number
  periodUnit: string
  holdings?: number
  noHoldings?: boolean
  holdingsMax?: number
  holdingsMin?: number
  initialHoldings?: number
  initialHoldingsMax?: number
  initialHoldingsMin?: number
  perkSecurities: string[]
}

enum EligibilityRuleFormKey {
  PERIOD = "period",
  PERIOD_UNIT = "periodUnit",
  HOLDINGS = "holdings",
  NO_HOLDINGS = "noHoldings",
  HOLDINGS_MAX = "holdingsMax",
  HOLDINGS_MIN = "holdingsMin",
  INITIAL_HOLDINGS = "initialHoldings",
  INITIAL_HOLDINGS_MAX = "initialHoldingsMax",
  INITIAL_HOLDINGS_MIN = "initialHoldingsMin",
  SECURITIES = "securities",
}

enum EligibilityRuleType {
  FREE = "free",
  EVERYONE_ELIGIBLE = "everyone-eligible",
  SINCE = "since",
  HOLDINGS = "holdings",
  INITIAL_HOLDINGS = "initialHoldings",
  SINCE_UNIT = "sinceUnit",
  SECURITY = "security",
}

enum EligibilityRuleExpression {
  EQUALS = "==",
  LESS_OR_EQUAL = "<=",
  GREATER_OR_EQUAL = ">=",
}

export const eligibilityRulesParser = (rule: string): EligibilityRuleParsed => {
  // Strip grouping parens — the rule language only uses them to group OR'd
  // security clauses, so flattening them keeps the term-splitter below
  // (which doesn't track precedence) working.
  const parsedRuleExpression = rule
    .replaceAll(logicSymbolsRegexp, ",")
    .replaceAll(" ", "")
    .replaceAll("(", "")
    .replaceAll(")", "")
    .trim()

  let securities = []

  return parsedRuleExpression.split(",").reduce(
    (acc: EligibilityRuleParsed, term) => {
      const [name, exp, valueStr] = term.split(booleanSymbolsRegexp)
      const value = parseInt(valueStr)

      let nameExpression = ""
      if (exp === EligibilityRuleExpression.LESS_OR_EQUAL)
        nameExpression = "Max"
      if (exp === EligibilityRuleExpression.GREATER_OR_EQUAL)
        nameExpression = "Min"

      switch (name) {
        case EligibilityRuleType.FREE:
        case EligibilityRuleType.EVERYONE_ELIGIBLE:
          acc.noHoldings = true
          break

        case EligibilityRuleType.SINCE:
          acc.period = value
          break

        case EligibilityRuleType.HOLDINGS:
          acc[`holdings${nameExpression}`] = value
          break

        case EligibilityRuleType.INITIAL_HOLDINGS:
          acc[`initialHoldings${nameExpression}`] = value
          break

        case EligibilityRuleType.SINCE_UNIT:
          acc.periodUnit = valueStr
          break

        case EligibilityRuleType.SECURITY:
          securities = [...securities, valueStr.replace(/'/g, "")]
          acc.perkSecurities = securities
          break
      }
      return acc
    },
    { perkSecurities: [] },
  )
}

export const eligibilityRulesToString = (
  rule: EligibilityRuleParsed,
): string => {
  const ruleKeys = Object.keys(rule)

  return ruleKeys.reduce((acc: string, key: string) => {
    let expression = ""

    switch (key) {
      case EligibilityRuleFormKey.NO_HOLDINGS:
        expression = EligibilityRuleType.EVERYONE_ELIGIBLE
        break

      case EligibilityRuleFormKey.PERIOD:
        expression = `since == ${rule[key]}`
        break

      case EligibilityRuleFormKey.PERIOD_UNIT:
        expression = `sinceUnit == '${rule[key]}'`
        break

      case EligibilityRuleFormKey.HOLDINGS:
        expression = `holdings ${EligibilityRuleExpression.EQUALS} ${rule[key]}`
        break

      case EligibilityRuleFormKey.HOLDINGS_MAX:
        expression = `holdings ${EligibilityRuleExpression.LESS_OR_EQUAL} ${rule[key]}`
        break

      case EligibilityRuleFormKey.HOLDINGS_MIN:
        expression = `holdings ${EligibilityRuleExpression.GREATER_OR_EQUAL} ${rule[key]}`
        break

      case EligibilityRuleFormKey.INITIAL_HOLDINGS:
        expression = `initialHoldings ${EligibilityRuleExpression.EQUALS} ${rule[key]}`
        break

      case EligibilityRuleFormKey.INITIAL_HOLDINGS_MAX:
        expression = `initialHoldings ${EligibilityRuleExpression.LESS_OR_EQUAL} ${rule[key]}`
        break

      case EligibilityRuleFormKey.INITIAL_HOLDINGS_MIN:
        expression = `initialHoldings ${EligibilityRuleExpression.GREATER_OR_EQUAL} ${rule[key]}`
        break

      case EligibilityRuleFormKey.SECURITIES:
        rule[key].map((el, index) => {
          expression = expression.concat(`security == '${el}'`)
          if (index !== rule[key].length - 1)
            expression = expression.concat(" OR ")
        })
        // Wrap multi-security OR clauses in parens so the trailing
        // AND <holdings/period> binds against the whole group, not just
        // the last security. Single-security rules don't need parens.
        if (rule[key].length > 1) {
          expression = `(${expression})`
        }
        break
    }

    if (!acc) acc = expression
    else acc = `${acc} AND ${expression}`

    return acc
  }, "")
}

export const eligibilityRuleFormParser = (object: {
  [key: string]: any
}): EligibilityRuleParsed => {
  // "Everyone eligible" maps to the backend's `free` rule. Ignore all other
  // shares-configuration fields so the serialized rule is just `free`.
  if (object.eligibilityType?.value === ELIGIBILITY_TYPE.EVERYONE_ELIGIBLE) {
    return { noHoldings: true } as EligibilityRuleParsed
  }

  const result: EligibilityRuleParsed = {}
  const objectKeys = Object.keys(object).sort()
  const filteredArray = objectKeys.filter(
    el => el !== EligibilityRuleFormKey.SECURITIES,
  )
  const newObjectKeys = [EligibilityRuleFormKey.SECURITIES, ...filteredArray]

  newObjectKeys.forEach(key => {
    let value: any

    switch (key) {
      case EligibilityRuleFormKey.SECURITIES:
        let securities = []
        object[key].map(el => securities.push(el.value))
        value = securities
        break

      case EligibilityRuleFormKey.PERIOD:
        value = object[key]
        break
      case EligibilityRuleFormKey.PERIOD_UNIT:
        value = object[key]?.value
        break

      case EligibilityRuleFormKey.HOLDINGS_MAX:
      case EligibilityRuleFormKey.HOLDINGS_MIN:
        value = object[key]?.value || object[key]
        break

      case EligibilityRuleFormKey.INITIAL_HOLDINGS_MIN:
      case EligibilityRuleFormKey.INITIAL_HOLDINGS_MAX:
        if (object[EligibilityRuleFormKey.PERIOD]) {
          value = object[key]
        }
        break
    }

    if (value != null) {
      key !== EligibilityRuleFormKey.PERIOD_UNIT &&
      key !== EligibilityRuleFormKey.SECURITIES
        ? (result[key] = parseInt(value))
        : (result[key] = value)
    }
  })
  return result
}

export const getRulesLogic = (rules: EligibilityRuleParsed) => {
  let rulesLogic: SelectOption = null!

  if (
    !!rules.holdingsMin &&
    rules.holdingsMin >= 0 &&
    !!rules.holdingsMax &&
    rules.holdingsMax >= 0
  ) {
    rulesLogic = createDropdownValue(RULES_LOGIC.BETWEEN, "BETWEEN")
  } else if (!!rules.holdingsMin && rules.holdingsMin >= 0) {
    rulesLogic = createDropdownValue(
      RULES_LOGIC.GREATER_THAN_OR_EQUAL_TO,
      "GREATER_THAN_OR_EQUAL_TO",
    )
  }

  return rulesLogic
}

export const getInitialHoldingsRulesLogic = (rules: EligibilityRuleParsed) => {
  let initialHoldingsRuleLogic: SelectOption = null!

  if (
    !!rules.initialHoldingsMin &&
    rules.initialHoldingsMin >= 0 &&
    !!rules.initialHoldingsMax &&
    rules.initialHoldingsMax >= 0
  ) {
    initialHoldingsRuleLogic = createDropdownValue(
      RULES_LOGIC.BETWEEN,
      "BETWEEN",
    )
  } else if (!!rules.initialHoldingsMin && rules.initialHoldingsMin >= 0) {
    initialHoldingsRuleLogic = createDropdownValue(
      RULES_LOGIC.GREATER_THAN_OR_EQUAL_TO,
      "GREATER_THAN_OR_EQUAL_TO",
    )
  }

  return initialHoldingsRuleLogic
}

export const getPeriodUnit = (rules: EligibilityRuleParsed) => {
  let periodUnit: SelectOption
  if (!!rules.periodUnit) {
    const str = rules.periodUnit.replace(/'/g, "")
    periodUnit = createDropdownValue(HOLDING_PERIOD[str], HOLDING_PERIOD[str])
  } else {
    periodUnit = createDropdownValue(HOLDING_PERIOD.NOW, null)
  }
  return periodUnit
}

export const getSecuritiesDropDown = (
  securitiesRules: string[],
  securities: Security[],
) => {
  const result = securities.filter(el => securitiesRules.includes(el.code))
  return result.map(el => {
    return createDropdownValue(el.exchange + "." + el.symbol, el.code)
  })
}
